import * as Papa from 'papaparse';
import { preparePapaParseWorker } from '~/utils/papaparse';

export interface ValidationResult {
    validCount: number;
    message: string;
}

export interface ValidationError {
    message: string;
}

export interface CsvValidatorConfig {
    maxFileSizeBytes: number;
    requiredHeadings: string[];
    validateHeadings?: (headings: string[]) => string | undefined;
    validateHeading?: (heading: string) => string | undefined;
}

export function formatFileSize(bytes: number): string {
    if (bytes >= 1024 ** 3) {
        return `${parseFloat((bytes / 1024 ** 3).toFixed(2))}GiB`;
    }
    if (bytes >= 1024 ** 2) {
        return `${parseFloat((bytes / 1024 ** 2).toFixed(2))}MiB`;
    }
    return `${parseFloat((bytes / 1024).toFixed(2))}KiB`;
}

// Trims only characters PHP trim() also strips, not String.trim()'s wider
// Unicode set, so the browser never accepts a heading the imports service rejects.
export const foldHeading = (heading: string): string => heading.replace(/^[ \t\n\r]+|[ \t\n\r]+$/g, '').toLowerCase();

const ERRORS_MESSAGES = {
    encoding: (rowIndex: number) => `Invalid character at row ${rowIndex}. Make sure your CSV has UTF-8 encoding.`,
    headingEmpty: () => 'Found an empty heading.',
    headingLength: (heading: string) => `Heading "${heading}" is too long.`,
    headingUnique: (heading: string) => `Heading "${heading}" must be unique.`,
    headingRequired: (heading: string) => `Heading ${heading} is required.`,
    invalidColumns: (rowIndex: number) => `Wrong number of values at row ${rowIndex}.`,
    failedToProcess: (rowIndex: number, message: string) =>
        `It was not possible to process row ${rowIndex}.${message ? ` ${message}` : ''}`,
};

export interface CsvValidator {
    validate: (file: File) => Promise<ValidationResult>;
    maxFileSizeBytes: number;
}

export function createCsvValidator(config: CsvValidatorConfig): CsvValidator {
    const validate = (file: File): Promise<ValidationResult> => {
        let errorMessage = '';

        const handleError = (message: string, parser: Papa.Parser) => {
            errorMessage = message;
            parser.abort();
        };

        return new Promise((resolve, reject: (reason: ValidationError) => void) => {
            if (!file?.type || file.type !== 'text/csv') {
                reject({
                    message: 'The file is not a CSV. Please use CSV file in order to proceed with import.',
                });
                return;
            }

            if (file.size > config.maxFileSizeBytes) {
                reject({
                    message: `Max file size is ${formatFileSize(config.maxFileSizeBytes)}.`,
                });
                return;
            }

            let rowsCount = 0;
            const csvHeadings: string[] = [];

            preparePapaParseWorker();

            Papa.parse(file, {
                delimiter: ',',
                worker: true,
                skipEmptyLines: true,
                step: (results: Papa.ParseStepResult<string[]>, parser: Papa.Parser) => {
                    rowsCount++;
                    if (results.errors.length) {
                        const firstError = results.errors[0];
                        handleError(
                            ERRORS_MESSAGES.failedToProcess(firstError?.row ?? rowsCount, firstError?.message ?? ''),
                            parser
                        );
                        return;
                    }

                    const data = results.data;

                    for (const value of data) {
                        if (value && value.indexOf('\uFFFD') >= 0) {
                            handleError(ERRORS_MESSAGES.encoding(rowsCount), parser);
                            return;
                        }
                    }

                    if (rowsCount === 1) {
                        const headings = data.map(foldHeading);

                        for (const required of config.requiredHeadings) {
                            if (!headings.includes(required)) {
                                handleError(ERRORS_MESSAGES.headingRequired(required), parser);
                                return;
                            }
                        }

                        if (config.validateHeadings) {
                            const customError = config.validateHeadings(headings);
                            if (customError) {
                                handleError(customError, parser);
                                return;
                            }
                        }

                        const seenHeadings = new Set<string>();

                        for (const rawHeading of data) {
                            const heading = foldHeading(rawHeading);

                            if (!heading) {
                                handleError(ERRORS_MESSAGES.headingEmpty(), parser);
                                return;
                            }

                            if (config.validateHeading) {
                                const customError = config.validateHeading(heading);
                                if (customError) {
                                    handleError(customError, parser);
                                    return;
                                }
                            }

                            if (heading.length > 80) {
                                handleError(ERRORS_MESSAGES.headingLength(rawHeading), parser);
                                return;
                            }

                            if (seenHeadings.has(heading)) {
                                handleError(ERRORS_MESSAGES.headingUnique(rawHeading), parser);
                                return;
                            }

                            seenHeadings.add(heading);
                            csvHeadings.push(heading);
                        }

                        return;
                    }

                    if (data.length !== csvHeadings.length) {
                        handleError(ERRORS_MESSAGES.invalidColumns(rowsCount), parser);
                        return;
                    }
                },
                complete: () => {
                    if (rowsCount <= 1 && !errorMessage) {
                        reject({
                            message:
                                'The CSV file does not contain any data. Please go back and make corrections to the file.',
                        });
                        return;
                    }

                    resolve({
                        validCount: rowsCount - 1,
                        message: errorMessage,
                    });
                },
                error: () => {
                    reject({
                        message: 'It was not possible to process the CSV file. Please contact support.',
                    });
                },
            });
        });
    };

    return { validate, maxFileSizeBytes: config.maxFileSizeBytes };
}
